import { Link } from 'react-router-dom'
import { Logo } from '@/components/Logo'

const SEGMENTS = [
  { name: 'Schools', desc: 'Age-appropriate cyber literacy for students, built for the classroom.' },
  { name: 'Enterprise', desc: 'Staff awareness training that people actually finish.' },
  { name: 'Financial Services', desc: 'Fraud and social-engineering awareness, regulator-ready.' },
  { name: 'Government', desc: 'Civil-servant cyber hygiene at national scale.' },
]

const PILLARS = [
  {
    title: 'Animated Storytelling',
    desc: 'Real cybersecurity lessons told through characters and stories rooted in everyday African life — not slideshows.',
    icon: '🎬',
  },
  {
    title: 'Interactive Assessment',
    desc: 'Scenario-based quizzes that test judgment, not just recall — "what would you actually do?"',
    icon: '🧠',
  },
  {
    title: 'Gamified Progress',
    desc: 'XP, badges, streaks, and certificates that make learning cybersecurity feel like an achievement.',
    icon: '🏅',
  },
  {
    title: 'AI Coach — Sarah',
    desc: 'A conversational cybersecurity coach to answer "wait, is this a scam?" moments, coming in a later phase.',
    icon: '💬',
  },
]

export function LandingPage() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cyber-grid bg-[length:32px_32px] opacity-20" />
        <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />
        <div className="absolute left-1/3 top-1/3 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute -bottom-24 left-0 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 flex justify-center">
              <Logo size={140} />
            </div>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">
              Stories. Skills. Safer Futures.
            </p>
            <span className="cyber-pill text-teal-200">
              <span className="cyber-pill-dot" />
              Built for Africa
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              Learn cybersecurity through <span className="gradient-text">stories</span>,{' '}
              <span className="gradient-text">challenges</span> &amp; interactive learning.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-white/70">
              CyberTales Learning turns cyber awareness into animated stories, scenario quizzes, and
              gamified progress — for schools, companies, financial institutions, and government
              organizations across Africa.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/login" className="cyber-btn-primary w-full sm:w-auto">
                Try the Demo
              </Link>
              <a href="#segments" className="cyber-btn-secondary w-full sm:w-auto">
                Who it's for
              </a>
            </div>
            <p className="mt-4 text-xs uppercase tracking-wide text-white/40">
              Zero-cost preview build — episodes shown are placeholder content
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p) => (
            <div key={p.title} className="cyber-panel p-6">
              <div className="text-3xl">{p.icon}</div>
              <h3 className="mt-4 font-display font-semibold text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-white/60">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="segments" className="border-t border-white/10 bg-navy-900/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-white sm:text-3xl">
            One platform, four audiences
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SEGMENTS.map((s) => (
              <div key={s.name} className="cyber-panel p-6 text-center">
                <h3 className="font-display font-semibold gradient-text">{s.name}</h3>
                <p className="mt-2 text-sm text-white/60">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
          See it for yourself
        </h2>
        <p className="mt-3 text-white/60">
          Log in as any of six demo roles — no signup, no credentials, no cost.
        </p>
        <Link to="/login" className="cyber-btn-primary mt-8 inline-flex">
          Launch Demo Login
        </Link>
      </section>
    </div>
  )
}
